import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeyValueDTO } from '@ms/core';
import { PrincipalService } from '@ms/core/auth/principal.service';
import { Observable } from 'rxjs';
import { CurrencyType } from '../shared/enum/currencyType.enum';
import { httpParams, logRequest } from '../shared/http-utils';
import { SumDTO } from '../shared/model/math.model';
import { PeriodFee } from '../shared/model/period-fee.model';
import { IPage } from '../shared/page/page.model';
import {
    IPageableStudentFee,
    MonthDebt,
    MonthRevenue,
    PaymentCalendar,
    StudentDebtDTO,
    StudentFee,
    StudentFeeSearch,
} from '../student-fees/student-fee.model';
import { BalanceTotals, CancelFee, FeeDetail } from './../student-fees/student-fee.model';

@Injectable({
    providedIn: 'root',
})
export class StudentFeeService {
    baseUrl = '/school-service/api/student-fees';

    constructor(
        private http: HttpClient,
        private principalService: PrincipalService
    ) {
        console.log('Student Fee service created');
    }

    createStudentFee(studentFee: StudentFee): Observable<HttpResponse<StudentFee>> {
        studentFee.schoolId = this.principalService.authenticatedUser.activeSchool.id;
        return this.http.post<StudentFee>(this.baseUrl, studentFee, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateStudentFee(studentFee: StudentFee): Observable<HttpResponse<StudentFee>> {
        if (!studentFee.schoolId) {
            studentFee.schoolId = this.principalService.authenticatedUser.activeSchool.id;
        }
        return this.http.put<StudentFee>(this.baseUrl, studentFee, { observe: 'response', headers: logRequest() });
    }

    approveStudentFeeWithMerge(
        studentFee: StudentFee,
        existingStudentFeeId: string
    ): Observable<HttpResponse<StudentFee>> {
        return this.http.post<StudentFee>(`${this.baseUrl}/approve/${existingStudentFeeId}`, studentFee, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    approveStudentFee(studentFee: StudentFee): Observable<HttpResponse<StudentFee>> {
        if (!studentFee.schoolId) {
            studentFee.schoolId = this.principalService.authenticatedUser.activeSchool.id;
        }
        return this.http.post<StudentFee>(`${this.baseUrl}/approve`, studentFee, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    unapproveStudentFee(studentFee: StudentFee): Observable<HttpResponse<StudentFee>> {
        if (!studentFee.schoolId) {
            studentFee.schoolId = this.principalService.authenticatedUser.activeSchool.id;
        }
        return this.http.patch<StudentFee>(`${this.baseUrl}/unapprove/${studentFee.id}`, null, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getStudentFeeById(id: string): Observable<HttpResponse<StudentFee>> {
        return this.http.get<StudentFee>(`${this.baseUrl}/${id}`, {
            observe: 'response',
        });
    }

    getCurrentTotalDebtById(id: string, date: string): Observable<HttpResponse<StudentFee>> {
        return this.http.get<StudentFee>(`${this.baseUrl}/total-debt/${id}/date/${date}`, {
            observe: 'response',
        });
    }

    calculateStudentFee(periodFee: PeriodFee, studentId: string): Observable<HttpResponse<FeeDetail>> {
        return this.http.post<FeeDetail>(`${this.baseUrl}/calculate/${studentId}`, periodFee, {
            observe: 'response',
            headers: logRequest(),
        });
    }
    calculateStudentFeeDiscount(periodFee: PeriodFee): Observable<HttpResponse<FeeDetail>> {
        return this.http.post<FeeDetail>(`${this.baseUrl}/calculate`, periodFee, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    searchStudentFeesWithPagination(
        studentFeeSearchDTO: StudentFeeSearch,
        params?: any
    ): Observable<HttpResponse<IPage<IPageableStudentFee>>> {
        studentFeeSearchDTO.schoolId = this.principalService.authenticatedUser.activeSchool.id;
        return this.http.post<IPage<IPageableStudentFee>>(`${this.baseUrl}/search/pageable`, studentFeeSearchDTO, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    makePayment(payment: MonthDebt, studentId: string): any {
        return this.http.post<StudentFee[]>(`${this.baseUrl}/pay/${studentId}`, payment, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getCalculatedBalanceById(id: string): Observable<HttpResponse<BalanceTotals>> {
        return this.http.get<BalanceTotals>(`${this.baseUrl}/${id}/calculate/balance`, {
            observe: 'response',
        });
    }

    getStudentsFeeByStudentId(
        studentId: string,
        academicPeriodId: string,
        savedStudentFee: string
    ): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.get<KeyValueDTO[]>(
            `${this.baseUrl}/acad-period/${academicPeriodId}/student/${studentId}?feeId=${savedStudentFee}`,
            {
                observe: 'response',
            }
        );
    }

    getStudentFeeTotalsById(studentFeeId: string): Observable<HttpResponse<StudentFee>> {
        return this.http.get<StudentFee>(`${this.baseUrl}/${studentFeeId}/totals`, {
            observe: 'response',
        });
    }

    deleteStudentFee(studentFeeId: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${studentFeeId}`, { observe: 'response', headers: logRequest() });
    }

    getStudentFeeDebt(studentFeeId: string): Observable<HttpResponse<CancelFee[]>> {
        return this.http.get<CancelFee[]>(`${this.baseUrl}/debts/${studentFeeId}`, {
            observe: 'response',
        });
    }

    cancelStudentFee(studentFeeId: string, cancelFee: CancelFee[]): Observable<HttpResponse<StudentFee>> {
        return this.http.post<StudentFee>(`${this.baseUrl}/cancel-fees/${studentFeeId}`, cancelFee, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getMonthDebts(
        payment: PaymentCalendar,
        details: FeeDetail[],
        grandTotal: number
    ): Observable<HttpResponse<MonthDebt[]>> {
        return this.http.post<MonthDebt[]>(
            `${this.baseUrl}/calculate-calendar`,
            { payment, details, grandTotal },
            {
                observe: 'response',
            }
        );
    }

    refund(studentId: string, value: MonthRevenue): any {
        return this.http.post<any>(`${this.baseUrl}/refund/${studentId}`, value, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getRefundedList(studentFeeId: string): Observable<HttpResponse<MonthRevenue[]>> {
        return this.http.get<MonthRevenue[]>(`${this.baseUrl}/${studentFeeId}/refunded`, {
            observe: 'response',
        });
    }
    getPaymentsList(studentFeeId: string): Observable<HttpResponse<MonthRevenue[]>> {
        return this.http.get<MonthRevenue[]>(`${this.baseUrl}/${studentFeeId}/payments`, {
            observe: 'response',
        });
    }

    getExistingStudentFee(
        studentFeeId: string,
        studentId: string,
        periodId: string,
        currencyType: CurrencyType
    ): Observable<HttpResponse<StudentFee>> {
        return this.http.post<StudentFee>(
            `${this.baseUrl}/existing-fee`,
            { studentFeeId, studentId, periodId, currencyType },
            {
                observe: 'response',
            }
        );
    }

    getStudentsExtraPayedAmount(studentId: string): Observable<HttpResponse<SumDTO>> {
        return this.http.get<SumDTO>(`${this.baseUrl}/student/${studentId}/extra-payed-amount`, {
            observe: 'response',
        });
    }

    getStudentsDebtOfOtherPeriods(
        studentId: string,
        exceptPeriodId: string
    ): Observable<HttpResponse<StudentDebtDTO[]>> {
        return this.http.get<StudentDebtDTO[]>(
            `${this.baseUrl}/other-debt/student/${studentId}/period/${exceptPeriodId}`,
            {
                observe: 'response',
            }
        );
    }

    getPDFReport(feeId: string): Observable<HttpResponse<any>> {
        return this.http.get(`${this.baseUrl}/${feeId}/report`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    cancelRefund(studentFeeId: string, refundId: string): any {
        return this.http.post<any>(`${this.baseUrl}/${studentFeeId}/refund/${refundId}/cancel`, {
            observe: 'response',
            headers: logRequest(),
        });
    }
}
